import { handle400Statuses, sendApiRequest } from '@services/api';
import { BASEURL } from '@services/network';
import { createAuthedEventSource } from '@utils/streamAuth';
import { logError, logNetRequest } from '@utils/usefulFuncs';

const SETTINGS_BLOCKLIST = new Set([
  'memory_key',
  'enableDailySummaries',
  'overrideQuestion',
]);

const QUESTION_RESERVED_KEYS = new Set([
  'question',
  'doc_ids',
  'agent_name',
  'context_prompt',
]);

const filterSettings = (settings) => {
  if (!settings) return undefined;
  const out = {};
  Object.keys(settings).forEach((key) => {
    if (!SETTINGS_BLOCKLIST.has(key) && settings[key] !== undefined && settings[key] !== null) {
      out[key] = settings[key];
    }
  });
  return Object.keys(out).length > 0 ? out : undefined;
};

const normalizeDocIds = (docIds) => {
  if (docIds == null) return undefined;
  if (Array.isArray(docIds)) {
    return docIds
      .map((d) => (typeof d === 'object' && d !== null ? d.id : d))
      .filter((d) => d != null);
  }
  return docIds;
};

const normalizeQuestion = (input) => {
  if (input == null) return null;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    return trimmed ? { question: trimmed } : null;
  }
  if (typeof input !== 'object') return null;
  if (!input.question || typeof input.question !== 'string') return null;
  const out = { question: input.question };
  if (input.doc_ids != null) {
    const docIds = normalizeDocIds(input.doc_ids);
    if (docIds && (!Array.isArray(docIds) || docIds.length > 0)) out.doc_ids = docIds;
  }
  if (input.agent_name) out.agent_name = input.agent_name;
  if (input.context_prompt) out.context_prompt = input.context_prompt;
  Object.keys(input).forEach((key) => {
    if (
      !QUESTION_RESERVED_KEYS.has(key) &&
      !SETTINGS_BLOCKLIST.has(key) &&
      input[key] !== undefined &&
      input[key] !== null
    ) {
      out[key] = input[key];
    }
  });
  return out;
};

const normalizeQuestions = (questions) => {
  if (!questions) return [];
  const arr = Array.isArray(questions) ? questions : [questions];
  return arr.map(normalizeQuestion).filter((q) => q !== null);
};

const buildRawQueryBody = ({ questions, ticker, agentName, docIds, settings }) => {
  const body = { questions: normalizeQuestions(questions) };
  if (ticker) body.ticker = ticker;
  if (agentName) body.agent_name = agentName;
  const normDocIds = normalizeDocIds(docIds);
  if (normDocIds && (!Array.isArray(normDocIds) || normDocIds.length > 0)) {
    body.doc_ids = normDocIds;
  }
  const filteredSettings = filterSettings(settings);
  if (filteredSettings) body.settings = filteredSettings;
  return body;
};

const buildTemplateBody = ({ modeType, templateId, docIds, modeParams, settings, ticker, useRfpResponseContext }) => {
  const body = {};
  if (templateId != null) body.template_id = templateId;
  else if (modeType) body.mode_type = modeType;
  const normDocIds = normalizeDocIds(docIds);
  if (normDocIds && (!Array.isArray(normDocIds) || normDocIds.length > 0)) {
    body.doc_ids = normDocIds;
  }
  if (modeParams && Object.keys(modeParams).length > 0) {
    body.mode_params = modeParams;
  }
  if (ticker) body.ticker = ticker;
  if (useRfpResponseContext != null) body.use_rfp_response_context = useRfpResponseContext;
  const filteredSettings = filterSettings(settings);
  if (filteredSettings) body.settings = filteredSettings;
  return body;
};

const hasAgentName = (body) => {
  if (body.agent_name) return true;
  return Array.isArray(body.questions) && body.questions.some((q) => q && q.agent_name);
};

const submitV2 = async (endpoint, body, label) => {
  const { status, data, error } = await sendApiRequest('post', endpoint, body);
  logNetRequest(label || endpoint, status, data, error);
  handle400Statuses(status);
  if (status === 200 && data) {
    return { status, runId: data.run_id ?? null, data, error: null };
  }
  return { status, runId: data?.run_id ?? null, data: data || null, error: error || data?.error || null };
};

export const submitQueries = ({ questions, ticker, agentName, docIds, settings }) => {
  const body = buildRawQueryBody({ questions, ticker, agentName, docIds, settings });
  return submitV2('v2/genai/queries', body, 'v2/genai/queries');
};

export const submitQueriesStream = ({ questions, ticker, agentName, docIds, settings }) => {
  const body = buildRawQueryBody({ questions, ticker, agentName, docIds, settings });
  return submitV2('v2/genai/queries/stream', body, 'v2/genai/queries/stream');
};

export const submitTemplate = ({ modeType, templateId, docIds, modeParams, settings, ticker, useRfpResponseContext }) => {
  const body = buildTemplateBody({ modeType, templateId, docIds, modeParams, settings, ticker, useRfpResponseContext });
  return submitV2('v2/genai/queries/template', body, 'v2/genai/queries/template');
};

export const submitTemplateStream = ({ modeType, templateId, docIds, modeParams, settings, ticker, useRfpResponseContext }) => {
  const body = buildTemplateBody({ modeType, templateId, docIds, modeParams, settings, ticker, useRfpResponseContext });
  return submitV2('v2/genai/queries/template/stream', body, 'v2/genai/queries/template/stream');
};

const buildCitationBody = ({ queryRunId, model, needContext = true }) => {
  const body = { query_run_id: queryRunId };
  const settings = {};
  if (model) settings.model = model;
  if (needContext != null) settings.need_context = needContext;
  if (Object.keys(settings).length > 0) body.settings = settings;
  return body;
};

const postV2 = async (endpoint, body, label) => {
  const { status, data, error } = await sendApiRequest('post', endpoint, body);
  logNetRequest(label || endpoint, status, data, error);
  handle400Statuses(status);
  return { status, data: data || null, error: error || data?.error || null };
};

export const submitCitation = ({ queryRunId, model, needContext }) => {
  const body = buildCitationBody({ queryRunId, model, needContext });
  return postV2('v2/genai/citations', body, 'v2/genai/citations');
};

export const submitCitationStream = async ({ queryRunId, model, needContext }) => {
  const body = buildCitationBody({ queryRunId, model, needContext });
  const result = await postV2('v2/genai/citations/stream', body, 'v2/genai/citations/stream');
  return {
    status: result.status,
    runId: result.data?.run_id ?? null,
    queryRunId: result.data?.query_run_id ?? null,
    data: result.data,
    error: result.error,
  };
};

export const submitQualityCheck = ({ queryRunId }) => {
  return postV2('v2/genai/quality_checks', { query_run_id: queryRunId }, 'v2/genai/quality_checks');
};

const buildSseUrl = (path, { runId, includeContext, includeAgentPlan }) => {
  const params = [`run_id=${encodeURIComponent(runId)}`];
  if (includeContext) params.push('include_context=true');
  if (includeAgentPlan) params.push('include_agent_plan=true');
  return `${BASEURL}/api/${path}?${params.join('&')}`;
};

const headersAsObject = (authHeaders) => {
  if (!authHeaders) return undefined;
  if (Array.isArray(authHeaders)) {
    return authHeaders.reduce((acc, [key, val]) => {
      acc[key] = val;
      return acc;
    }, {});
  }
  return authHeaders;
};

const openSseSource = (url, authHeaders, listeners) => {
  const source = createAuthedEventSource(url, {
    headers: headersAsObject(authHeaders),
  });
  Object.entries(listeners || {}).forEach(([eventName, handler]) => {
    if (handler) source.addEventListener(eventName, handler);
  });
  return {
    source,
    close: () => {
      try {
        source.close();
      } catch (e) {
        logError('genaiQueriesV2 sse close', e);
      }
    },
  };
};

export const openStreamEvents = ({ runId, includeContext, includeAgentPlan, authHeaders, onOpen, onMessage, onComplete, onError, onRefining }) => {
  if (!runId) {
    onError?.({ error: 'Missing run_id' });
    return { close: () => {} };
  }
  const url = buildSseUrl('v2/genai/queries/stream_events', { runId, includeContext, includeAgentPlan });
  return openSseSource(url, authHeaders, {
    open: onOpen,
    message: onMessage,
    complete: onComplete,
    error: onError,
    refining: onRefining,
  });
};

export const openCompleteEvent = ({ runId, includeContext, includeAgentPlan, authHeaders, onComplete, onError, onHeartbeat }) => {
  if (!runId) {
    onError?.({ error: 'Missing run_id' });
    return { close: () => {} };
  }
  const url = buildSseUrl('v2/genai/queries/complete_event', { runId, includeContext, includeAgentPlan });
  return openSseSource(url, authHeaders, {
    message: onHeartbeat,
    complete: onComplete,
    error: onError,
  });
};

export const __testing = {
  buildRawQueryBody,
  buildTemplateBody,
  buildCitationBody,
  normalizeQuestions,
  normalizeDocIds,
  filterSettings,
  hasAgentName,
  SETTINGS_BLOCKLIST,
  QUESTION_RESERVED_KEYS,
  buildSseUrl,
};
